/**
 * ShipmentLinksPicker - Seção Relacionamentos do modal de remessa.
 *
 * Visual da seção Relacionamentos do modal de Tarefas (rótulo pequeno em maiúsculas
 * com ícone + campo de seleção), porém aceitando VÁRIOS registros por tipo: os
 * escolhidos aparecem como etiquetas removíveis abaixo de cada campo.
 * Busca reutiliza RPCs existentes: get_service_orders_paginated (O.S.) e global_search
 * (demais tipos). Gravação por set_shipment_link (allowlist + bloqueios no servidor).
 *
 * RELEASE: 2026-10-16 v2.0.0 - Leiaute Relacionamentos (padrão Tarefas) com múltiplos
 *          vínculos por tipo; O.S. concluída/cancelada/aguardando revisão/em correção
 *          não é listada (também rejeitada em set_shipment_link v1.1.0).
 * RELEASE: 2026-10-16 v1.0.0 - Versão inicial.
 */
import { useEffect, useState } from 'react';
import { Briefcase, Car, ChevronsUpDown, ClipboardList, ListChecks, Loader2, User, X, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useGlobalSearch, type GlobalSearchType } from '@/hooks/useGlobalSearch';
import { useServiceOrdersList, type ServiceOrderStatus } from '@/hooks/useServiceOrders';
import type { ShipmentEntityType } from '@/hooks/useShipments';

export interface ShipmentLinkValue { entity_type: ShipmentEntityType; entity_id: string; label: string }

export const LINK_TYPE_LABEL: Record<ShipmentEntityType, string> = {
  service_order: 'O.S.', deal: 'Negócio', task: 'Tarefa', customer: 'Cliente', vehicle: 'Veículo',
};

/** Situações de O.S. em que não faz sentido enviar equipamento (espelha set_shipment_link). */
/** v2.1.0: só O.S. Aberta é vinculável (espelha set_shipment_link v1.3.0). */
export const isServiceOrderLinkable = (status: ServiceOrderStatus) => status === 'open';

/** Escopo da pesquisa global por tipo de vínculo. */
const GLOBAL_TYPES: Partial<Record<ShipmentEntityType, GlobalSearchType[]>> = {
  deal: ['deal'], task: ['task:normal', 'task:retention', 'task:legal'], customer: ['customer'], vehicle: ['vehicle'],
};

const FIELDS: { type: ShipmentEntityType; label: string; icon: LucideIcon }[] = [
  { type: 'service_order', label: 'Ordem de serviço', icon: ClipboardList },
  { type: 'deal', label: 'Negócio', icon: Briefcase },
  { type: 'task', label: 'Tarefa', icon: ListChecks },
  { type: 'customer', label: 'Cliente', icon: User },
  { type: 'vehicle', label: 'Veículo', icon: Car },
];

interface Props {
  value: ShipmentLinkValue[];
  onAdd: (link: ShipmentLinkValue) => void;
  onRemove: (link: ShipmentLinkValue) => void;
  disabled?: boolean;
}

export function ShipmentLinksPicker({ value, onAdd, onRemove, disabled }: Props) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {FIELDS.map((f) => (
        <LinkField key={f.type} field={f} disabled={disabled} onAdd={onAdd} onRemove={onRemove}
          value={value.filter((v) => v.entity_type === f.type)} />
      ))}
    </div>
  );
}

/** Campo de um tipo de relacionamento (seleção múltipla). */
function LinkField({ field, value, onAdd, onRemove, disabled }: {
  field: (typeof FIELDS)[number]; value: ShipmentLinkValue[];
  onAdd: Props['onAdd']; onRemove: Props['onRemove']; disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const Icon = field.icon;
  const isOs = field.type === 'service_order';

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(term.trim()), 300);
    return () => window.clearTimeout(t);
  }, [term]);

  const osQuery = useServiceOrdersList({
    search: debounced, status: null, serviceTypeId: null, providerId: null, categoryId: null, phaseId: null,
    deadlineBucket: null, dateField: 'created_at', dateFrom: null, dateTo: null, sortField: 'created_at',
    sortDirection: 'desc', page: 1, pageSize: 30,
  }, open && isOs);
  const global = useGlobalSearch(term, open && !isOs, GLOBAL_TYPES[field.type] ?? ['deal']);

  const options: { id: string; title: string; subtitle: string | null }[] = isOs
    ? (osQuery.data?.items ?? []).filter((o) => isServiceOrderLinkable(o.status))
        .map((o) => ({ id: o.id, title: o.number, subtitle: o.customer_name }))
    : [...global.deals, ...global.tasksNormal, ...global.tasksRetention, ...global.tasksLegal, ...global.customers, ...global.vehicles]
        .map((r) => ({ id: r.entity_id, title: r.title ?? '—', subtitle: r.subtitle }));
  const loading = isOs ? osQuery.isFetching : global.isLoading;
  const chosen = new Set(value.map((v) => v.entity_id));

  return (
    <div className="space-y-1.5">
      <Label className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" />{field.label}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" role="combobox" disabled={disabled}
            className="h-9 w-full justify-between font-normal text-muted-foreground">
            <span className="truncate">{value.length ? `${value.length} selecionado(s) · adicionar` : `Selecionar ${LINK_TYPE_LABEL[field.type]}`}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] min-w-72 p-2" align="start">
          <Input autoFocus className="h-9" value={term} maxLength={60} onChange={(e) => setTerm(e.target.value)}
            placeholder={isOs ? 'Buscar número, cliente, placa...' : 'Digite ao menos 3 letras...'} />
          {isOs && <p className="mt-1 px-1 text-[11px] text-muted-foreground">O.S. concluídas, canceladas, aguardando revisão ou em correção não aparecem.</p>}
          <div className="mt-2 max-h-64 overflow-y-auto">
            {loading && <Loader2 className="mx-auto my-3 h-4 w-4 animate-spin text-muted-foreground" />}
            {!loading && options.length === 0 && <p className="py-3 text-center text-xs text-muted-foreground">Nenhum resultado.</p>}
            {options.map((o) => (
              <button key={o.id} type="button" disabled={chosen.has(o.id)}
                className="w-full rounded px-2 py-1.5 text-left text-sm hover:bg-muted disabled:opacity-50"
                onClick={() => { onAdd({ entity_type: field.type, entity_id: o.id, label: o.title }); setTerm(''); }}>
                <p className="truncate font-medium">{o.title}</p>
                {o.subtitle && <p className="truncate text-xs text-muted-foreground">{o.subtitle}</p>}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((v) => (
            <Badge key={v.entity_id} variant="secondary" className="max-w-full gap-1 pr-1">
              <span className="truncate">{v.label}</span>
              {!disabled && (
                <button type="button" aria-label={`Remover ${v.label}`} className="rounded p-0.5 hover:bg-background" onClick={() => onRemove(v)}>
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
